//hooks
import {  useState } from "react"
import { useError } from "@contexts/ErrorContext"
//components
import { Button } from "@components/ui/buttons/Button"
import Select from 'react-select'
import { Input } from "@components/ui/inputs/Input"
import Label from "@components/ui/labels/Label"
import { APIMessage } from "@components/ui/messages/APIMessage"

/**
 * Modal to select where in EditableTable to add a new row. Can optionally pass required fields to get user input, like lat/lng or estimated chain, which is needed for some table calculations. Flexible for any job control component.
 * 
 * @param {DropdownOptionAddRow[]} dropdownOptions - fill the dropdown with AGMs, subchains, etc, so user can select after which one they want the new row
 * @param {Boolean} showDialog 
 * @param {Function} onSubmit 
 * @param {string[]} requiredFields - any fields that require user input - has db formatting like "Site_Name", "Est_Chain"
 * @param {Object} columnNameMap - maps db formatting to input label format for required fields - for example, to convert "Site_Name" to "Site Name"
 * 
 * @returns {JSX.Element}
 */
export const AddRowDialog = ({dropdownOptions, showDialog, onSubmit, requiredFields = [], columnNameMap}) => {
    const [position, setPosition] = useState("bottom") //where in the table the row should be added
    const [afterRowOption, setAfterRowOption] = useState("") //the specific row, if any, after which to add the new row

    const [userInput, setUserInput] = useState({})
    const [validationErrors, setValidationErrors] = useState({})

    const {showError} = useError()

    const handleSubmit = () => {
        const isValid = validate()

        if(!isValid) return

        const insertIndex = position === "top" ? 0 
            : position === "bottom" ? dropdownOptions.length
            : afterRowOption.index + 1 //add one, since we want to insert AFTER the selected item

        onSubmit(insertIndex, userInput);
        showDialog(false);
    }

    const handleUserInput = (field, value) => {
        setUserInput(prevData => {
            const newData = {...prevData}

            newData[field] = value

            return newData
        })
    }

    const validate = () => {
        const errors = {}

        for(const field of requiredFields){
            if(!userInput[field] || userInput[field].length === 0){
                errors[field] = "Required"
            }
        }

        //if there are errors, set state to errors and return false
        if(Object.keys(errors).length > 0){
            setValidationErrors(errors)
            showError("Could not add row - some required fields are missing")
            return false
        }

        setValidationErrors({})

        return true
    }

    return (
        <>
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white p-8 rounded-lg w-50 mx-4 flex flex-col items-center max-h-[90vh] overflow-y-auto">
                    <p className="text-md text-center font-bold">Add New Row</p>

                    {/* radio buttons to select new row placement: after a specific row, or the top or bottom of table */}
                    <div className="space-y-4 my-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="position"
                                    value="top"
                                    checked={position === "top"}
                                    onChange={(e) => setPosition(e.target.value)}
                                />
                                <span className="text-sm font-semibold text-gray-700">Top of table</span>
                            </label>

                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="position"
                                    value="bottom"
                                    checked={position === "bottom"}
                                    onChange={(e) => setPosition(e.target.value)}
                                />
                                <span className="text-sm font-semibold text-gray-700">Bottom of table</span>
                            </label>
                            
                            <div className="flex items-center">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        name="position"
                                        value="after"
                                        checked={position === "after"}
                                        onChange={(e) => setPosition(e.target.value)}
                                        className="mr-2"
                                    />
                                </label>

                                <span className="mr-4 text-sm font-semibold text-gray-700">After row:</span>

                                <div className="w-36 sm:w-56">

                                    <Select
                                        options={dropdownOptions}
                                        onChange={(x) => setAfterRowOption(x)}
                                        styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                        }}
                                        menuPosition="fixed"
                                        value={afterRowOption}
                                        isDisabled={position !== "after"}
                                    />
                                </div>

                            </div>


                    </div>

                    {/* show an input for each required field */}
                    {requiredFields && (
                        <div className="space-y-2 my-4">
                            <p className="text-sm text-center font-semibold">Required Fields</p>
                            {requiredFields.map(field => (
                                <div key={`input-${field}`}>
                                    <Label>{columnNameMap[field] || field}</Label>
                                    <Input
                                        type="text"
                                        name={field}
                                        value={userInput[field] || ""}
                                        onChange={(e) => handleUserInput(field, e.target.value)}
                                        className="mr-2"
                                        placeholder={`Enter ${columnNameMap[field]}...`}
                                        error={validationErrors[field]}
                                        required
                                    /> 
                                    <APIMessage message={validationErrors[field]} isError={true} className="my-0 text-xs"/>
                                    
                                </div>
                            ))}
                        </div>
                    )}

                    
                    <div>
                        <div className='flex mt-5 space-x-10'>
                            <Button
                                variant="success"
                                onClick={handleSubmit}
                                className='w-max mr-2'
                            >
                                Add
                            </Button>
                            <Button
                                variant="danger"
                                onClick={() => showDialog(false)}
                                className='w-max mr-2'
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>

                </div>
            </div>
        </>
    )
}